import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AlertMessageContainer } from './styles';
import { ReactComponent as MessageCross } from 'assets/images/messageCross.svg';
import messageDone from 'assets/images/messageDone.svg';
import { colors } from 'constants/colors';
import { useRecoilState } from 'recoil';
import { globalErrorState, globalMessageState } from 'states/CommonStates/atoms';
import { useLocation } from 'react-router-dom';

type AlertMessageProps = {
    children: React.ReactNode; // Content to be displayed inside the AlertMessage
    showMessage: boolean; // Indicates whether to show the message
    onClose: () => void; // Function to call when the message is closed
    backgroundColor?: string; // Optional background color
    messageType?: string;  // Optional meassage type - green message dafault
    width?: string; // Width of the AlertMessage
    positionRight?: string; // Bottom position
    positionTop?: string; // Left position

    // Optional properties for different screen sizes
    widthTablet?: string;
    positionRightTablet?: string;
    positionTopTablet?: string;

    widthMini?: string;
    positionRightMini?: string;
    positionTopMini?: string;

    widthMobile?: string;
    positionRightMobile?: string;
    positionTopMobile?: string;
    hideMessage?: boolean; // Autohide message
};

const debug = false;


export const AlertMessage: React.FC<AlertMessageProps> = ({
    showMessage,
    onClose, // Add the onClose prop
    children,
    backgroundColor,
    messageType,
    width,
    positionRight,
    positionTop,

    widthTablet,
    positionRightTablet,
    positionTopTablet,
    
    widthMini,
    positionRightMini,
    positionTopMini,

    positionTopMobile,
    hideMessage = true,
}) => {
    const [isVisible, setIsVisible] = useState(showMessage); // Start visibility based on showMessage prop
    const [isFadingOut, setIsFadingOut] = useState(false);
    
    const [errorState] = useRecoilState(globalErrorState);
    const [messageState] = useRecoilState(globalMessageState);

    const messageTypeSelector = messageType ? messageType : 'message';
    
    const location = useLocation();
    

    let backgroundColorSelector = backgroundColor
    ? backgroundColor
    : messageTypeSelector === 'error'
    ? colors.errorRed
    : colors.optionBackground;


    if (debug) {
        console.log ('showMessage = ' + showMessage);
    }

    const onCloseRef = useRef(onClose);

    useEffect(() => {
        onCloseRef.current = onClose;
    }, [onClose]);
    
    const handleClose = useCallback(() => {
        setIsFadingOut(true);
    
        setTimeout(() => {
            setIsVisible(false);
            onCloseRef.current();
        }, 200);
    }, []);

    const isPathMatch = (targetPath: string | null) => {
        if (!targetPath) return true;
        return location.pathname.startsWith(targetPath);
    };

    const currentTargetPath =
    messageType === 'error'
        ? errorState.targetPath
        : messageState.targetPath;

const shouldShow = showMessage && isPathMatch(currentTargetPath);

useEffect(() => {
    if (shouldShow) {
        setIsVisible(true);
        setIsFadingOut(false);

        if (hideMessage) {
            const timer = setTimeout(() => {
                handleClose();
            }, 5000);

            return () => clearTimeout(timer);
        }

        return;
    }

    setIsFadingOut(true);

    const fadeOutTimer = setTimeout(() => {
        setIsVisible(false);
    }, 200);

    return () => clearTimeout(fadeOutTimer);
}, [shouldShow, hideMessage, handleClose]);

if (!isVisible || !shouldShow) return null;


    
    return (
        <AlertMessageContainer
            className={`dateHint ${isFadingOut ? 'fade-out' : ''}`}
            width={width}
            backgroundColor={backgroundColorSelector}
            positionRight={positionRight}
            positionTop={positionTop}

            widthTablet={widthTablet}
            positionRightTablet={positionRightTablet}
            positionTopTablet={positionTopTablet}

            widthMini={widthMini}
            positionRightMini={positionRightMini}
            positionTopMini={positionTopMini}

            positionTopMobile={positionTopMobile}
        >
            <MessageCross
                className="messageCross"
                role="img"
                aria-label="Close"
                onClick={handleClose} // Close the alert on click
            />
                {messageTypeSelector === 'message' && (
                    <img src={messageDone} alt="Done" className="messageDone" />
                )}

            {children} {/* Render the children content */}
        </AlertMessageContainer>
    );
};

export default AlertMessage;
